/**
 * <sarky-hero-tabs>
 *
 * Progressive enhancement for the three hero collection links in
 * snippets/sarky-hero-search-tabs.liquid. Without this script (or if it
 * fails to load), the tabs are plain, real `<a href>` links to the real
 * collection pages - a genuine no-JS fallback, not a broken button.
 *
 * With JS, this intercepts the click, updates the ARIA tablist state on
 * these three links itself, and drives the SARKY product rail directly
 * below the hero (sections/sarky-product-rail.liquid, in its
 * `controlled_externally` mode) via one explicit, well-known id -
 * `#sarky-product-rail-primary` (set on `data-rail-target`) - never a
 * generic query that could reach into a different rail instance
 * elsewhere on the page.
 *
 * If that target rail isn't found (e.g. this snippet ends up on a page
 * without the primary rail), clicks are left alone and behave as plain
 * navigation - no broken interaction.
 */
class SarkyHeroTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (!this.tabs.length) return;

    this.railTargetId = this.getAttribute('data-rail-target');

    this.handleClick = (event) => {
      const rail = this.railTargetId && document.getElementById(this.railTargetId);
      if (!rail || typeof rail.activateByCollectionHandle !== 'function') return;

      event.preventDefault();
      this.activate(event.currentTarget, rail);
    };

    this.handleKeydown = (event) => {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (nextIndex === null) return;
      event.preventDefault();

      const nextTab = this.tabs[nextIndex];
      nextTab.focus();

      const rail = this.railTargetId && document.getElementById(this.railTargetId);
      if (rail && typeof rail.activateByCollectionHandle === 'function') {
        this.activate(nextTab, rail);
      }
    };

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleClick);
      tab.addEventListener('keydown', this.handleKeydown);
    });
  }

  disconnectedCallback() {
    if (!this.tabs) return;
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.handleClick);
      tab.removeEventListener('keydown', this.handleKeydown);
    });
  }

  activate(tab, rail) {
    this.tabs.forEach((t) => {
      const isTarget = t === tab;
      t.setAttribute('aria-selected', String(isTarget));
      t.tabIndex = isTarget ? 0 : -1;
      t.classList.toggle('is-active', isTarget);
    });

    const handle = tab.getAttribute('data-collection');
    if (handle) rail.activateByCollectionHandle(handle);
  }
}

if (!customElements.get('sarky-hero-tabs')) {
  customElements.define('sarky-hero-tabs', SarkyHeroTabs);
}
